import { Search } from '@mui/icons-material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {
    AccordionDetails,
    Checkbox,
    Divider,
    FormControlLabel,
    type InputProps,
    Link,
    Skeleton,
    Stack,
    type SxProps,
    TextField,
    type Theme,
    Tooltip,
    Typography,
} from '@mui/material';
import type { AccordionProps } from '@mui/material/Accordion';
import MuiAccordion from '@mui/material/Accordion';
import type { AccordionSummaryProps } from '@mui/material/AccordionSummary';
import MuiAccordionSummary, { accordionSummaryClasses } from '@mui/material/AccordionSummary';
import { styled } from '@mui/material/styles';
import { type Dispatch, type SetStateAction, useMemo, useState } from 'react';
import Chip from '/@/components/bayer/chip';
import colors from '/@/components/theme-provider/colors';
import settings from '/@/core/settings';
import { _ } from '/@/locale';

const Accordion = styled((props: AccordionProps) => <MuiAccordion disableGutters elevation={0} square {...props} />)(
    ({ theme }) => ({
        p: 0,
        borderBottom: `1px solid ${theme.palette.divider}`,
        '&:last-child': {
            borderBottom: 0,
        },
        '&::before': {
            display: 'none',
        },
    })
);

const AccordionSummary = styled((props: AccordionSummaryProps) => (
    <MuiAccordionSummary expandIcon={<KeyboardArrowDownIcon fontSize='24px' />} {...props} />
))(() => ({
    [`&.${accordionSummaryClasses.root}`]: {
        padding: 0,
    },
    [`& .${accordionSummaryClasses.content}`]: {
        margin: 0,
        alignItems: 'center',
    },
}));

const MultiLevelCheckbox = ({
    InputProps,
    items,
    selectedItems,
    setSelectedItems,
    verbose,
    loading = false,
    sx,
}: {
    InputProps: InputProps;
    items: { label: string; children: { id: number; label: string; disabled?: string }[] }[];
    selectedItems: Set<number>;
    setSelectedItems: Dispatch<SetStateAction<Set<number>>>;
    verbose: string;
    loading?: boolean;
    sx?: SxProps<Theme>;
}) => {
    const [search, setSearch] = useState('');

    const toggleSelectedItem = (id: number, checked: boolean) => {
        setSelectedItems((prev) => {
            const next = new Set(prev);
            if (checked) {
                next.add(id);
            } else {
                next.delete(id);
            }
            return next;
        });
    };

    const selectedItemData = useMemo(() => {
        return items.flatMap((item) => {
            return item.children.filter((child) => selectedItems.has(child.id));
        });
    }, [items, selectedItems]);

    const filteredItems = useMemo(() => {
        const sanitizedSearch = search.toLowerCase().trim();
        if (sanitizedSearch === '') return items;
        return items
            .filter(
                (item) =>
                    item.label.toLowerCase().includes(sanitizedSearch) ||
                    item.children?.some((child) => child.label.toLowerCase().includes(sanitizedSearch))
            )
            .map((item) => {
                return {
                    label: item.label,
                    children: item.label.toLowerCase().includes(sanitizedSearch)
                        ? item.children
                        : item.children?.filter((child) => child.label.toLowerCase().includes(sanitizedSearch)),
                };
            });
    }, [items, search]);

    return (
        <Stack spacing={3} sx={[{ width: '100%' }, ...(Array.isArray(sx) ? sx : [sx])]}>
            {InputProps && (
                <TextField
                    InputProps={{ startAdornment: <Search /> }}
                    color='secondary'
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    {...InputProps}
                    sx={{ flexShrink: 0 }}
                />
            )}

            <Stack overflow='auto' flex={1} minHeight={0} px={3}>
                {filteredItems.map((item) => (
                    <Accordion key={item.label}>
                        <AccordionSummary>
                            <Typography component='span' variant='subtitle1' fontWeight='bold' color='primary'>
                                {item.label} ({item.children?.length || 0})
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ py: 0 }}>
                            <Stack ml={2}>
                                {item.children?.map((child, index) => {
                                    const isActive = selectedItems.has(child.id);

                                    return (
                                        <Tooltip key={child.id} arrow title={child.disabled || ''}>
                                            <FormControlLabel
                                                key={child.id}
                                                control={
                                                    <Checkbox
                                                        value={child.id}
                                                        checked={isActive}
                                                        size='large'
                                                        onChange={(event, checked) => {
                                                            event.stopPropagation();
                                                            toggleSelectedItem(child.id, checked);
                                                        }}
                                                        color='secondary'
                                                        disabled={!!child.disabled}
                                                    />
                                                }
                                                label={child.label}
                                                sx={[
                                                    index !== (item.children?.length || 0) - 1 && {
                                                        borderBottom: (t) => `1px solid ${t.palette.divider}`,
                                                    },
                                                    isActive && {
                                                        backgroundColor: '#F5F5F5',
                                                    },
                                                    {
                                                        '& .MuiTypography-root': {
                                                            color: (t) => t.palette.primary.main,
                                                        },
                                                    },
                                                ]}
                                            />
                                        </Tooltip>
                                    );
                                })}
                            </Stack>
                        </AccordionDetails>
                    </Accordion>
                ))}
                {loading && !filteredItems.length && (
                    <Stack overflow='auto' spacing={0} divider={<Divider />}>
                        {Array.from({ length: 5 }).map((_, i) => (
                            <Stack key={`skeleton-${i}`} py={1.5}>
                                <Skeleton variant='text' height={24} />
                            </Stack>
                        ))}
                    </Stack>
                )}

                {filteredItems.length === 0 && filteredItems.length !== items.length && (
                    <Typography variant='body1' color='primary' alignSelf='start'>
                        {_('No se encontraron resultados. Revisa el nombre o apellido ingresado.')}
                    </Typography>
                )}
            </Stack>

            <Stack spacing={1} sx={{ flexShrink: 0 }}>
                <Stack spacing={1} direction='row' justifyContent='space-between' alignItems='center'>
                    <Typography variant='caption' color='primary.mui.inactive'>
                        {`${_('Seleccionaste')} ${selectedItems.size} ${verbose}`}
                    </Typography>
                    <Link color='primary' sx={{ cursor: 'pointer' }} onClick={() => setSelectedItems(new Set())}>
                        {_('Borrar todo')}
                    </Link>
                </Stack>
                <Stack direction='row' spacing={1} flexWrap='wrap' overflow='auto' maxHeight='100px' useFlexGap>
                    {selectedItemData.map((item) => (
                        <Chip
                            key={item.id}
                            label={item.label}
                            onDelete={() =>
                                setSelectedItems((prev) => {
                                    const next = new Set(prev);
                                    next.delete(item.id);
                                    return next;
                                })
                            }
                            sx={{
                                width: 'fit-content',
                                background: colors.themes[settings.app.theme].mui.primary.infoBackground,
                            }}
                        />
                    ))}
                </Stack>
            </Stack>
        </Stack>
    );
};

export default MultiLevelCheckbox;
